import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  CardHeader,
  Box,
  IconButton,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { saveAs } from 'file-saver';
import { apiGet } from '../../utils/apiClient';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { useTranslation } from '../../i18n';

interface RevenueData {
  name: string;
  value: number;
}

interface RevenueChartProps {
  title: string;
  data: RevenueData[];
  monthAndYear: Dayjs | null;
  setMonthAndYear: (date: Dayjs | null) => void;
  fetchChartData: (date: Dayjs | null) => void;
}

const RevenueChart: React.FC<RevenueChartProps> = ({
  title,
  data,
  monthAndYear,
  setMonthAndYear,
  fetchChartData,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [exportLoading, setExportLoading] = useState(false);

  const handleExportRevenue = async () => {
    if (!monthAndYear || !monthAndYear.isValid()) return;
    setExportLoading(true);
    try {
      const month = monthAndYear.month() + 1;
      const year = monthAndYear.year();
      const response = await apiGet<Blob>(
        `/report/export-revenue-report?month=${month}&year=${year}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([response as any], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(
        blob,
        `revenue_report_${year}-${String(month).padStart(2, '0')}.xlsx`,
      );
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        boxShadow: theme.shadows[2],
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <CardHeader
        title={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                  field: {
                    clearable: true,
                    onClear: () => {
                      setMonthAndYear(null);
                      fetchChartData(null);
                    },
                  },
                }}
                format="MM/YYYY"
                label={t('report.chart_selectMonth')}
                views={['month', 'year']}
                value={monthAndYear}
                onChange={(newDate) => {
                  if (newDate?.isValid()) {
                    setMonthAndYear(newDate);
                    fetchChartData(newDate);
                  }
                }}
              />
            </LocalizationProvider>
            <IconButton
              title={t('report.chart_downloadRevenue')}
              aria-label={t('report.chart_downloadRevenue')}
              onClick={handleExportRevenue}
              loading={exportLoading}
              disabled={
                !monthAndYear || !monthAndYear.isValid() || exportLoading
              }
              sx={{ ml: 1 }}
            >
              <FileDownloadIcon />
            </IconButton>
          </Box>
        }
      />
      <CardContent sx={{ flexGrow: 1, px: 2, pt: 0, pb: 2 }}>
        <Box sx={{ width: '100%', height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{
                top: 30,
                right: 30,
                left: 40,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                tickFormatter={(value) =>
                  new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND',
                    maximumFractionDigits: 0,
                  }).format(value)
                }
              />
              <Tooltip
                formatter={(value: number) =>
                  new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND',
                    maximumFractionDigits: 0,
                  }).format(value)
                }
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 8,
                  boxShadow: theme.shadows[3],
                  border: 'none',
                }}
              />
              <Bar
                dataKey="value"
                fill={theme.palette.primary.main}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RevenueChart;
